import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Upload, Trash2, Loader2, Plus } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const GalleryManage = ({ authToken }) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [newImage, setNewImage] = useState({
    title: '',
    category: 'Residential',
    file: null,
  });

  const categories = ['Residential', 'Commercial', 'Bathrooms', 'Kitchens', 'Offices'];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch images:', error);
      setMessage({ type: 'error', text: 'Failed to load gallery images' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
      return;
    }
    setMessage({ type: '', text: '' });
    setNewImage((prev) => ({ ...prev, file }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!newImage.file || !newImage.title?.trim()) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    setIsUploading(true);
    setMessage({ type: '', text: '' });
    console.log("Gallery authToken:", authToken);  //dsvjdsvjdnbfvkfgkjngfjk

    try {
      // 1) Get Cloudinary signature
      const sigResponse = await axios.get(
        `${API}/cloudinary/signature?folder=gallery`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      const sig = sigResponse.data;

      if (!sig?.api_key || !sig?.signature || !sig?.cloud_name) {
        throw new Error('Invalid Cloudinary signature response from server');
      }

      // 2) Prepare FormData
      const formData = new FormData();
      formData.append('file', newImage.file);
      formData.append("upload_preset", sig.upload_preset || "brightedge");
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      // 3) Upload to Cloudinary
      const cloudName = sig.cloud_name || "dkcmdjcem";
      const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

      const res = await fetch(url, { method: "POST", body: formData });

      // READ ONCE AS TEXT to avoid "body stream already read" error
      const rawText = await res.text();
      
      let result = {};
      try {
        result = JSON.parse(rawText);
      } catch (parseError) {
        // Fallback for non-JSON responses
      }

      if (!res.ok) {
        throw new Error(result?.error?.message || `Cloudinary upload failed: ${rawText}`);
      }

      if (!result?.secure_url) {
        throw new Error('Cloudinary did not return secure_url');
      }

      // 4) Save to database
      await axios.post(
        `${API}/admin/gallery`,
        {
          title: newImage.title.trim(),
          category: newImage.category,
          image_url: result.secure_url,
          cloudinary_public_id: result.public_id,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
      setNewImage({ title: '', category: 'Residential', file: null });
      setIsDialogOpen(false);
      fetchImages();

      // Reset file input UI
      const fileInput = document.getElementById('file');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Upload failed:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to upload image' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setMessage({ type: 'success', text: 'Image deleted successfully!' });
      fetchImages();
    } catch (error) {
      console.error('Delete failed:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to delete image' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-primary mb-2">Gallery Management</h2>
          <p className="text-muted-foreground">
            Upload and manage gallery images ({images.length} total)
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              Add Image
            </Button>
          </DialogTrigger>

          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload New Image</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Image Title</Label>
                <Input
                  id="title"
                  value={newImage.title}
                  onChange={(e) =>
                    setNewImage((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="e.g., Clean Kitchen"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={newImage.category}
                  onValueChange={(value) =>
                    setNewImage((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="file">Image File</Label>
                <input
                  id="file"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  required
                />
                <Button
                  type="button"
                  onClick={() => document.getElementById('file')?.click()}
                  className="w-full"
                  variant="outline"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {newImage.file ? newImage.file.name : 'Choose File'}
                </Button>
              </div>

              <Button
                type="submit"
                disabled={isUploading}
                className="w-full rounded-full bg-primary"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload Image'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {message.text && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {images.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-border">
          <Upload className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-primary mb-2">No images yet</h3>
          <p className="text-muted-foreground mb-4">Upload your first gallery image</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="group relative overflow-hidden rounded-xl border border-border bg-white"
            >
              <div className="aspect-square">
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4">
                <p className="text-white font-medium text-center mb-1">{image.title}</p>
                <p className="text-white/80 text-sm mb-4">{image.category}</p>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(image.id)}
                  className="rounded-full"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GalleryManage;
